import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import Modal from "../Modal";
import Button from "../Button";
import Input from "../Input";
import Icon from "../Icon";
import InputCheckbox from "../InputCheckbox";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { convertIntToMoney } from "../../helpers/conversions";
import { convertToCardNumber, convertToCardExpiry, convertToCep, convertToCpfCnpj, convertToDigits } from "../../helpers/masks";
import { CreditCoin } from "../CreditCoin";

import "./styles.scss";

const paymentOptions = [
    { value: 'credit_card', label: 'Cartão de crédito', icon: 'card' },
    { value: 'pix', label: 'Pix', icon: 'pix' },
    { value: 'boleto', label: 'Boleto', icon: 'receipt-text' }
];

/**
 * Compra de créditos (BDM-1056).
 *
 * Roda no Asaas, o mesmo gateway da assinatura. Ele tokeniza no servidor, então
 * os dados do cartão sobem para a nossa API, vão para o gateway e não ficam
 * guardados em lugar nenhum — o que sobra é o token que o Asaas devolve, usado
 * depois pela recarga automática. É o mesmo caminho do checkout da assinatura.
 */
export const ModalBuyCredits = ({ show, close, onPurchased }) => {
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState({ packages: [], payment_methods: [], min_purchase_cents: 0, validity_months: 12 });

    const [packageId, setPackageId] = useState('');
    const [coupon, setCoupon] = useState('');
    const [quote, setQuote] = useState(null);
    const [couponError, setCouponError] = useState('');

    const [paymentMethod, setPaymentMethod] = useState('credit_card');
    const [savedCardId, setSavedCardId] = useState('');
    const [saveCard, setSaveCard] = useState(true);

    const [cardNumber, setCardNumber] = useState('');
    const [cardHolder, setCardHolder] = useState('');
    const [cardExpiration, setCardExpiration] = useState('');
    const [cardCvv, setCardCvv] = useState('');

    // O Asaas exige os dados do titular junto da cobrança e recusa sem eles.
    const [holderDocument, setHolderDocument] = useState('');
    const [holderPostalCode, setHolderPostalCode] = useState('');
    const [holderAddressNumber, setHolderAddressNumber] = useState('');

    const [result, setResult] = useState(null);
    const pollRef = useRef(null);

    useEffect(() => {
        if (show) {
            getOptions();
        } else {
            clean();
        }
    }, [show]);

    // Pix e boleto só confirmam pelo webhook: enquanto a compra estiver pendente
    // na tela, perguntamos ao backend se ela virou paga, para o cliente não ter
    // que fechar e recarregar para ver o saldo entrar.
    useEffect(() => {
        if (!result || result.status !== 'pending') {
            return;
        }

        pollRef.current = setInterval(() => {
            api.get('/credit/purchase').then(res => {
                const updated = (res.data.data ?? []).find(item => item.id === result.id);

                if (updated && updated.status !== 'pending') {
                    setResult(updated);

                    if (updated.status === 'paid') {
                        toast.success('Pagamento confirmado — créditos adicionados ao saldo');
                        if (onPurchased) onPurchased(updated);
                    }
                }
            }).catch(() => {});
        }, 5000);

        return () => clearInterval(pollRef.current);
    }, [result]);

    useEffect(() => {
        if (packageId) {
            getQuote();
        }
    }, [packageId]);

    const getOptions = () => {
        setLoading(true);

        api.get('/credit/purchase/options').then(res => {
            const resource = res.data.resource;
            setOptions(resource);

            const highlighted = resource.packages.find(item => item.highlight) ?? resource.packages[0];
            if (highlighted) setPackageId(highlighted.id);

            const defaultCard = resource.payment_methods.find(item => item.default) ?? resource.payment_methods[0];
            if (defaultCard) setSavedCardId(defaultCard.id);
        }).catch(renderError).then(() => setLoading(false));
    }

    const getQuote = (code = coupon) => {
        setCouponError('');

        api.post('/credit/purchase/quote', {
            package_id: packageId,
            coupon_code: code || null
        }).then(res => {
            setQuote(res.data.resource);
        }).catch(error => {
            if (error?.response?.status === 422) {
                setCouponError(error.response.data.message);
                // O cupom recusado não pode travar a compra: a simulação volta
                // para o preço cheio e o aviso fica no campo.
                api.post('/credit/purchase/quote', { package_id: packageId })
                    .then(res => setQuote(res.data.resource))
                    .catch(() => {});
                return;
            }
            renderError(error);
        });
    }

    const buy = async () => {
        setLoading(true);

        try {
            const payload = {
                package_id: packageId,
                payment_method: paymentMethod,
                coupon_code: couponError ? null : (coupon || null)
            };

            if (paymentMethod === 'credit_card') {
                if (usingNewCard) {
                    const [month, year] = String(cardExpiration).split('/').map(part => part.trim());

                    payload.card = {
                        number: cardNumber.replace(/\D/g, ''),
                        holder_name: cardHolder,
                        expiry_month: month,
                        expiry_year: year?.length === 2 ? `20${year}` : year,
                        ccv: cardCvv
                    };

                    payload.holder = {
                        cpf_cnpj: holderDocument.replace(/\D/g, ''),
                        postal_code: holderPostalCode.replace(/\D/g, ''),
                        address_number: holderAddressNumber
                    };

                    payload.save_card = saveCard;
                } else {
                    payload.payment_method_id = savedCardId;
                }
            }

            const res = await api.post('/credit/purchase', payload);
            const purchase = res.data.resource;

            setResult(purchase);

            if (purchase.status === 'paid') {
                toast.success('Créditos adicionados ao saldo');
                if (onPurchased) onPurchased(purchase);
            }
        } catch (error) {
            renderError(error);
        }

        setLoading(false);
    }

    const clean = () => {
        // packageId volta a vazio para a simulação rodar de novo na próxima
        // abertura: com o mesmo id o efeito não disparava e o botão ficava em
        // "Pagar" sem valor.
        setPackageId('');
        setQuote(null);
        setCoupon('');
        setCouponError('');
        setResult(null);
        setCardNumber('');
        setCardHolder('');
        setCardExpiration('');
        setCardCvv('');
        setHolderDocument('');
        setHolderPostalCode('');
        setHolderAddressNumber('');
    }

    // O desconto de volume é o que diferencia um pacote do outro: cada um é
    // comparado com o crédito mais caro da vitrine, que é sempre o do menor
    // pacote.
    const priciestUnit = options.packages.reduce((max, item) => Math.max(max, item.unit_price_cents), 0);
    const discountOf = item => (priciestUnit > 0 ? Math.round((1 - item.unit_price_cents / priciestUnit) * 100) : 0);

    const hasSavedCards = options.payment_methods.length > 0;
    const usingNewCard = !hasSavedCards || savedCardId === 'new';

    const cardComplete = cardNumber.replace(/\D/g, '').length >= 13
        && cardHolder.trim()
        && /^\d{2}\/\d{2}$/.test(cardExpiration)
        && cardCvv.length >= 3
        && holderDocument.replace(/[^0-9A-Za-z]/g, '').length >= 11
        && holderPostalCode.replace(/\D/g, '').length === 8
        && holderAddressNumber.trim();

    const canBuy = !loading
        && packageId
        && (paymentMethod !== 'credit_card' || (usingNewCard ? cardComplete : savedCardId));

    return (
        <Modal
            className={`modal-buy-credits`}
            size={`big`}
            title={result ? 'Compra registrada' : 'Comprar créditos'}
            show={show}
            close={close}
            footer={!result &&
                <>
                    <Button type={`secondary`} size={`small`} text={`Cancelar`} action={close} />
                    <Button
                        type={`primary`}
                        size={`small`}
                        text={quote ? `Pagar ${convertIntToMoney(quote.amount_cents, true)}` : 'Pagar'}
                        action={buy}
                        loading={loading}
                        disabled={!canBuy}
                    />
                </>
            }
        >
            {result &&
                <div className="section buy-result">
                    {result.status === 'paid' &&
                        <>
                            <Icon name={`check`} size={40} />
                            <h3>Créditos liberados</h3>
                            <p>{(result.credits + result.bonus_credits).toLocaleString('pt-BR')} créditos já estão no saldo da plataforma.</p>
                        </>
                    }
                    {result.status !== 'paid' &&
                        <>
                            <Icon name={result.status === 'pending' ? 'clock' : 'alert'} size={40} />
                            <h3>{result.status === 'pending' ? 'Aguardando o pagamento' : 'Pagamento não concluído'}</h3>
                            <p>
                                {result.status !== 'pending'
                                    ? 'A cobrança foi cancelada ou venceu. Nenhum crédito foi adicionado; você pode fazer uma nova compra.'
                                    : (result.payment_method === 'pix'
                                        ? 'Pague o Pix para liberar os créditos — o saldo entra assim que o banco confirmar. Pode deixar esta janela aberta: ela avisa quando o pagamento cair.'
                                        : 'Os créditos entram na compensação do boleto, em até 3 dias úteis.')}
                            </p>
                            {result.status === 'pending' && result.meta?.pix_qr_image &&
                                <img className="pix-image" src={`data:image/png;base64,${result.meta.pix_qr_image}`} alt="QR Code do Pix" width={180} height={180} />
                            }
                            {result.status === 'pending' && result.pix_qr_code &&
                                <div className="pix-code">
                                    <span>{result.pix_qr_code}</span>
                                    <Button type={`secondary`} size={`small`} text={`Copiar código`} action={() => {
                                        navigator.clipboard.writeText(result.pix_qr_code);
                                        toast.success('Código copiado');
                                    }} />
                                </div>
                            }
                            {result.status === 'pending' && result.invoice_url &&
                                <a className="invoice-link" href={result.invoice_url} target="_blank" rel="noopener noreferrer">
                                    {result.payment_method === 'pix' ? 'Abrir cobrança' : 'Abrir boleto'}
                                </a>
                            }
                        </>
                    }
                    <Button type={`primary`} size={`small`} text={`Fechar`} action={close} />
                </div>
            }

            {!result &&
                <>
                    <div className="section">

                    {!loading && options.packages.length === 0 &&
                        <div className="box-alert-info empty-packages">
                            Nenhum pacote de créditos está disponível no momento. Fale com a Brinde.me para liberar a compra.
                        </div>
                    }

                    <div className="packages">
                        {options.packages.map(item => (
                            <button
                                type="button"
                                key={item.id}
                                className={`package ${packageId === item.id ? 'selected' : ''}`}
                                onClick={() => setPackageId(item.id)}
                            >
                                {item.highlight && <span className="tag">Mais usado</span>}
                                <span className="check"><Icon name={`check`} size={12} strokeWidth={3} /></span>
                                <span className="name">{item.name}</span>
                                <span className="credits">
                                    <CreditCoin size={18} />
                                    {item.credits.toLocaleString('pt-BR')}
                                </span>
                                <span className="price">{convertIntToMoney(item.price_cents, true)}</span>
                                <span className="unit">{convertIntToMoney(item.unit_price_cents, true)} por crédito</span>
                                {discountOf(item) > 0
                                    ? <span className="saving">{discountOf(item)}% mais barato</span>
                                    : <span className="saving base">preço base</span>
                                }
                            </button>
                        ))}
                    </div>

                    <div className="row">
                        <div className="col-6">
                            <div className="coupon-field">
                                <Input
                                    type={`text`}
                                    label={`Cupom de desconto`}
                                    value={coupon}
                                    change={value => setCoupon(value.toUpperCase())}
                                    action={() => getQuote()}
                                />
                                <Button type={`secondary`} text={`Aplicar`} action={() => getQuote()} disabled={!coupon} />
                            </div>
                            {couponError && <p className="field-error">{couponError}</p>}
                        </div>
                        <div className="col-6">
                            <p className="validity-note">
                                Os créditos valem {options.validity_months} meses a partir da compra.
                            </p>
                        </div>
                    </div>
                    </div>

                    <div className="section">
                    <div className="payment-options">
                        {paymentOptions.map(option => (
                            <button
                                type="button"
                                key={option.value}
                                className={`payment-option ${paymentMethod === option.value ? 'selected' : ''}`}
                                onClick={() => setPaymentMethod(option.value)}
                            >
                                <Icon name={option.icon} size={18} />
                                {option.label}
                            </button>
                        ))}
                    </div>

                    {paymentMethod === 'credit_card' &&
                        <div className="card-form">
                            {hasSavedCards &&
                                <div className="saved-cards">
                                    {options.payment_methods.map(card => (
                                        <button
                                            type="button"
                                            key={card.id}
                                            className={`saved-card ${savedCardId === card.id ? 'selected' : ''}`}
                                            onClick={() => setSavedCardId(card.id)}
                                        >
                                            {card.brand} •••• {card.last4}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        className={`saved-card ${savedCardId === 'new' ? 'selected' : ''}`}
                                        onClick={() => setSavedCardId('new')}
                                    >
                                        Outro cartão
                                    </button>
                                </div>
                            }

                            {usingNewCard &&
                                <>
                                    <p className="field-group-label">Dados do cartão</p>
                                    <div className="row">
                                        <div className="col-6">
                                            <Input type={`text`} label={`Número do cartão`} value={cardNumber}
                                                change={value => setCardNumber(convertToCardNumber(value))}
                                                autoComplete={`cc-number`} left={<Icon name={`card`} size={16} />} />
                                        </div>
                                        <div className="col-6">
                                            <Input type={`text`} label={`Nome impresso no cartão`} value={cardHolder}
                                                change={setCardHolder}
                                                autoComplete={`cc-name`} left={<Icon name={`user`} size={16} />} />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <Input type={`text`} label={`Validade (MM/AA)`} value={cardExpiration}
                                                change={value => setCardExpiration(convertToCardExpiry(value))}
                                                autoComplete={`cc-exp`} left={<Icon name={`calendar`} size={16} />} />
                                        </div>
                                        <div className="col-6">
                                            <Input type={`text`} label={`CVV`} value={cardCvv}
                                                change={value => setCardCvv(convertToDigits(value, 4))}
                                                autoComplete={`cc-csc`} left={<Icon name={`lock`} size={16} />} />
                                        </div>
                                    </div>

                                    <p className="field-group-label">Titular do cartão</p>
                                    <div className="row">
                                        <div className="col-4">
                                            <Input type={`text`} label={`CPF/CNPJ`} value={holderDocument}
                                                change={value => setHolderDocument(convertToCpfCnpj(value))}
                                                left={<Icon name={`contact`} size={16} />} />
                                        </div>
                                        <div className="col-4">
                                            <Input type={`text`} label={`CEP`} value={holderPostalCode}
                                                change={value => setHolderPostalCode(convertToCep(value))}
                                                left={<Icon name={`card-location`} size={16} />} />
                                        </div>
                                        <div className="col-4">
                                            <Input type={`text`} label={`Número do endereço`} value={holderAddressNumber}
                                                change={setHolderAddressNumber}
                                                left={<Icon name={`company`} size={16} />} />
                                        </div>
                                    </div>

                                    <button type="button" className={`save-card ${saveCard ? 'on' : ''}`} onClick={() => setSaveCard(!saveCard)}>
                                        <InputCheckbox active={saveCard} />
                                        <span>
                                            <b>Salvar este cartão</b>
                                            É o cartão que a recarga automática usa, e a próxima compra não pede os dados de novo.
                                        </span>
                                    </button>
                                </>
                            }
                        </div>
                    }

                    </div>

                    {quote &&
                        <div className="section summary">
                            {/* Sem desconto nem bônus, o subtotal repetiria o
                                total na linha de baixo — duas vezes o mesmo
                                número não informa nada. */}
                            {(quote.discount_cents > 0 || quote.bonus_credits > 0) &&
                                <div className="line">
                                    <span>{quote.credits.toLocaleString('pt-BR')} créditos</span>
                                    <b>{convertIntToMoney(quote.amount_cents + quote.discount_cents, true)}</b>
                                </div>
                            }
                            {quote.discount_cents > 0 &&
                                <div className="line discount">
                                    <span>Cupom {quote.coupon_code}</span>
                                    <b>− {convertIntToMoney(quote.discount_cents, true)}</b>
                                </div>
                            }
                            {quote.bonus_credits > 0 &&
                                <div className="line">
                                    <span>Créditos bônus</span>
                                    <b>+ {quote.bonus_credits.toLocaleString('pt-BR')}</b>
                                </div>
                            }
                            <div className="total">
                                <span className="label">
                                    <b>Total</b>
                                    {quote.total_credits.toLocaleString('pt-BR')} créditos
                                </span>
                                <b className="amount">{convertIntToMoney(quote.amount_cents, true)}</b>
                            </div>
                        </div>
                    }
                </>
            }
        </Modal>
    );
}

export default ModalBuyCredits;
